// src/utils/axiosInstance.js
import axios from 'axios';
import { BASE_API_URL } from '../config';
import authService from '../services/authService'; 

const axiosInstance = axios.create({
  baseURL: BASE_API_URL,
  timeout: 15000, // افزایش زمان انتظار برای سرورهای OnRender
  headers: {
    'Content-Type': 'application/json',
  },
});

// مدیریت صف برای درخواست‌های همزمان در حین رفرش توکن
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        error ? prom.reject(error) : prom.resolve(token);
    });
    failedQueue = [];
};

// ----------------------------------------------------
// 🟢 رهگیر درخواست (Request Interceptor) - اصلاح شده
// ----------------------------------------------------
axiosInstance.interceptors.request.use(
  (config) => {
    const authData = authService.getAuthTokens(); 
    
    // ✅ هوشمندسازی: اگر authData رشته بود از خودش استفاده کن، اگر آبجکت بود از .access
    const token = typeof authData === 'string' ? authData : authData?.access;

    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
        // console.log(`[Axios] Token attached to ${config.url}`); 
    } else {
        console.warn(`[Axios] No Token found for ${config.url}`);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ----------------------------------------------------
// 🟢 رهگیر پاسخ (Response Interceptor) - مدیریت 401 و Refresh
// ----------------------------------------------------
axiosInstance.interceptors.response.use(
    (response) => response, 
    async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
            
            // اگر در حال تلاش برای رفرش هستیم و باز هم 401 گرفتیم، یعنی رفرش توکن هم منقضی شده
            if (originalRequest.url.includes('/token/refresh/')) {
                 authService.logout();
                 return Promise.reject(error);
            }
            
            originalRequest._retry = true; 
            
            if (!isRefreshing) {
                isRefreshing = true;
                const authData = authService.getAuthTokens();
                // استخراج رفرش توکن (با فرض اینکه در localStorage با نام refresh_token ذخیره شده)
                const refreshToken = localStorage.getItem('refresh_token');

                if (!refreshToken) {
                    authService.logout();
                    isRefreshing = false;
                    return Promise.reject(error);
                }

                try {
                    const res = await axios.post(`${BASE_API_URL}/token/refresh/`, { refresh: refreshToken });
                    const newAccess = res.data.access;
                    
                    // ذخیره توکن جدید (مطابق با متد setAuthData شما)
                    authService.setAuthData({ token: newAccess, refresh_token: refreshToken });
                    
                    originalRequest.headers['Authorization'] = `Bearer ${newAccess}`;
                    processQueue(null, newAccess);
                    isRefreshing = false;
                    return axiosInstance(originalRequest);
                } catch (err) {
                    authService.logout();
                    processQueue(err, null);
                    isRefreshing = false;
                    return Promise.reject(err);
                }
            }

            // نگه داشتن سایر درخواست‌ها در صف
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(token => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                return axiosInstance(originalRequest);
            }).catch(err => Promise.reject(err));
        }
        return Promise.reject(error);
    }
);

export default axiosInstance;